/**
 * DeltaDetailView - Vue arborescente NEED : Match > Catégorie > Lignes VENTE
 * Comme un Google Sheet avec les groupes dépliables
 */
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, Copy, Check, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { resolveEventByApi } from '../../utils/resolveEventName';
import { getMatchTimeFR } from '../../data/wc2026Calendar';

const DeltaDetailView = ({ data = [], allTickets = [], project = '' }) => {
  const [expandedMatches, setExpandedMatches] = useState(new Set());
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [copiedKey, setCopiedKey] = useState(null);
  const [mode, setMode] = useState('need'); // 'need', 'stock', 'all'

  const toggleMatch = (key) => {
    setExpandedMatches(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleCat = (key) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Build tree: Match > Category > tickets
  const tree = useMemo(() => {
    const isCancelled = (t) => {
      const s = (t.statut || '').toUpperCase();
      return s === 'ANNULEE' || s === 'ANNULÉ' || s === 'ANNULE';
    };
    const filtered = mode === 'need'
      ? allTickets.filter(t => t.type === 'VENTE' && !t.attribue && !isCancelled(t))
      : mode === 'stock'
        ? allTickets.filter(t => t.type === 'ACHAT' && !t.attribue && !isCancelled(t))
        : allTickets.filter(t => !t.attribue && !isCancelled(t));

    const getMatchNum = (ev) => {
      const m = (ev || '').match(/M(\d+)/i);
      return m ? parseInt(m[1], 10) : null;
    };

    const byMatch = {};
    for (const t of filtered) {
      const num = getMatchNum(t.evenement);
      if (num === null) continue;
      if (!byMatch[num]) {
        byMatch[num] = { matchNum: num, evenement: t.evenement || '', dateEvenement: t.date_evenement || '', lieu: t.lieu || '', ville: t.ville || '', tickets: [] };
      }
      byMatch[num].tickets.push(t);
    }

    return Object.values(byMatch)
      .sort((a, b) => a.matchNum - b.matchNum)
      .map(match => {
        const byCat = {};
        for (const t of match.tickets) {
          // Normalize: CATEGORY X → CATEGORIE X, case insensitive
          let cat = (t.categorie || 'SANS CATÉGORIE').trim();
          cat = cat.replace(/^CATEGORY\s*/i, 'CATEGORIE ');
          if (!byCat[cat]) byCat[cat] = [];
          byCat[cat].push(t);
        }
        const categories = Object.entries(byCat)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([cat, tickets]) => {
            // Compute totalQty: ACHAT - VENTE in 'all' mode (delta), simple sum otherwise
            let totalQty;
            if (mode === 'all') {
              const achatQty = tickets.filter(t => t.type === 'ACHAT').reduce((s, t) => s + (parseInt(t.qty, 10) || 1), 0);
              const venteQty = tickets.filter(t => t.type === 'VENTE').reduce((s, t) => s + (parseInt(t.qty, 10) || 1), 0);
              totalQty = achatQty - venteQty;
            } else {
              totalQty = tickets.reduce((s, t) => s + (parseInt(t.qty, 10) || 1), 0);
            }
            return {
              name: cat,
              totalQty,
              tickets: tickets.sort((a, b) => {
                const ra = (a.ref_vente || '').toString();
                const rb = (b.ref_vente || '').toString();
                return rb.localeCompare(ra, undefined, { numeric: true });
              }),
            };
          });
        const matchTotalQty = categories.reduce((s, c) => s + c.totalQty, 0);
        return { ...match, categories, matchTotalQty };
      })
      .filter(m => m.categories.length > 0);
  }, [allTickets, mode]);

  // Expand / Collapse all
  const expandAll = () => {
    const allMatchKeys = new Set(tree.map(m => `m${m.matchNum}`));
    const allCatKeys = new Set();
    tree.forEach(m => m.categories.forEach(c => allCatKeys.add(`m${m.matchNum}_${c.name}`)));
    setExpandedMatches(allMatchKeys);
    setExpandedCats(allCatKeys);
  };

  const collapseAll = () => {
    setExpandedMatches(new Set());
    setExpandedCats(new Set());
  };

  // Copy text
  const doCopy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
    toast.success('Copié !');
  };

  // Build text for copying
  const typeLabel = mode === 'need' ? 'REF VENTE' : 'REF ACHAT';
  const buildAllText = () => {
    const header = mode === 'need'
      ? 'MATCH\tCATÉGORIE\tREF VENTE\tQTY\tPRIX'
      : mode === 'stock'
        ? 'MATCH\tCATÉGORIE\tSOURCE\tBLOCK\tRANG\tPLACE\tEMAIL COMPTE'
        : 'MATCH\tCATÉGORIE\tTYPE\tREF VENTE\tQTY\tBLOCK\tRANG\tPLACE\tPRIX';
    const lines = [header];
    for (const match of tree) {
      for (const cat of match.categories) {
        for (const t of cat.tickets) {
          if (mode === 'need') {
            lines.push(`${match.evenement}\t${cat.name}\t${t.ref_vente || '-'}\t${t.qty || 1}\t${t.prix || ''}`);
          } else if (mode === 'stock') {
            lines.push(`${match.evenement}\t${cat.name}\t${t.source || '-'}\t${t.block || ''}\t${t.rang || ''}\t${t.place || ''}\t${t.email_compte || ''}`);
          } else {
            lines.push(`${match.evenement}\t${cat.name}\t${t.type}\t${t.ref_vente || '-'}\t${t.qty || 1}\t${t.block || ''}\t${t.rang || ''}\t${t.place || ''}\t${t.prix || ''}`);
          }
        }
      }
    }
    return lines.join('\n');
  };

  const buildMatchText = (match) => {
    return match.categories.flatMap(cat => cat.tickets.map(t => 
      mode === 'need'
        ? `${match.evenement}\t${cat.name}\t${t.ref_vente || '-'}\t${t.qty || 1}\t${t.prix || ''}`
        : mode === 'stock'
          ? `${match.evenement}\t${cat.name}\t${t.source || '-'}\t${t.block || ''}\t${t.rang || ''}\t${t.place || ''}\t${t.email_compte || ''}`
          : `${match.evenement}\t${cat.name}\t${t.type}\t${t.ref_vente || '-'}\t${t.qty || 1}\t${t.block || ''}\t${t.rang || ''}\t${t.place || ''}\t${t.prix || ''}`
    )).join('\n');
  };

  const buildCatText = (match, cat) => {
    return cat.tickets.map(t =>
      mode === 'need'
        ? `${match.evenement}\t${cat.name}\t${t.ref_vente || '-'}\t${t.qty || 1}\t${t.prix || ''}`
        : mode === 'stock'
          ? `${match.evenement}\t${cat.name}\t${t.source || '-'}\t${t.block || ''}\t${t.rang || ''}\t${t.place || ''}\t${t.email_compte || ''}`
          : `${match.evenement}\t${cat.name}\t${t.type}\t${t.ref_vente || '-'}\t${t.qty || 1}\t${t.block || ''}\t${t.rang || ''}\t${t.place || ''}\t${t.prix || ''}`
    ).join('\n');
  };

  // Export CSV
  const exportCSV = () => {
    const rows = mode === 'need'
      ? [['Match #', 'Match', 'Catégorie', 'Ref Vente', 'Qty', 'Prix', 'Client', 'Source']]
      : mode === 'stock'
        ? [['Match #', 'Match', 'Catégorie', 'Source', 'Block', 'Rang', 'Place', 'Email Compte']]
        : [['Match #', 'Match', 'Catégorie', 'Type', 'Ref Vente', 'Qty', 'Block', 'Rang', 'Place', 'Prix', 'Source']];
    for (const match of tree) {
      const matchLabel = String(match.matchNum);
      for (const cat of match.categories) {
        for (const t of cat.tickets) {
          if (mode === 'need') {
            rows.push([matchLabel, match.evenement, cat.name, t.ref_vente || '', t.qty || 1, t.prix || '', t.nom_client || '', t.source || '']);
          } else if (mode === 'stock') {
            rows.push([matchLabel, match.evenement, cat.name, t.source || '', t.block || '', t.rang || '', t.place || '', t.email_compte || '']);
          } else {
            rows.push([matchLabel, match.evenement, cat.name, t.type, t.ref_vente || '', t.qty || 1, t.block || '', t.rang || '', t.place || '', t.prix || '', t.source || '']);
          }
        }
      }
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `delta_${mode}_detail_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exporté !');
  };

  const totalCount = tree.reduce((s, m) => s + m.matchTotalQty, 0);

  if (tree.length === 0 && mode === 'need') {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <Button size="sm" variant={mode === 'need' ? 'default' : 'outline'} onClick={() => { setMode('need'); setExpandedMatches(new Set()); setExpandedCats(new Set()); }} className={`h-7 text-xs px-3 ${mode === 'need' ? 'bg-red-600 hover:bg-red-700' : 'text-red-400 border-red-400/30'}`}>NEED</Button>
            <Button size="sm" variant={mode === 'stock' ? 'default' : 'outline'} onClick={() => { setMode('stock'); setExpandedMatches(new Set()); setExpandedCats(new Set()); }} className={`h-7 text-xs px-3 ${mode === 'stock' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-400 border-emerald-400/30'}`}>STOCK</Button>
            <Button size="sm" variant={mode === 'all' ? 'default' : 'outline'} onClick={() => { setMode('all'); setExpandedMatches(new Set()); setExpandedCats(new Set()); }} className={`h-7 text-xs px-3 ${mode === 'all' ? 'bg-[#C4A052] hover:bg-[#D4B86A] text-black' : 'text-[#C4A052] border-[#C4A052]/30'}`}>TOUT</Button>
          </div>
        </div>
        <div className="text-center py-12 text-zinc-500 text-sm">Aucune vente non-attribuée</div>
      </div>
    );
  }

  return (
    <div data-testid="delta-detail-view">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Button size="sm" variant={mode === 'need' ? 'default' : 'outline'} onClick={() => { setMode('need'); setExpandedMatches(new Set()); setExpandedCats(new Set()); }} className={`h-7 text-xs px-3 ${mode === 'need' ? 'bg-red-600 hover:bg-red-700' : 'text-red-400 border-red-400/30'}`} data-testid="mode-need-btn">NEED</Button>
            <Button size="sm" variant={mode === 'stock' ? 'default' : 'outline'} onClick={() => { setMode('stock'); setExpandedMatches(new Set()); setExpandedCats(new Set()); }} className={`h-7 text-xs px-3 ${mode === 'stock' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-400 border-emerald-400/30'}`} data-testid="mode-stock-btn">STOCK</Button>
            <Button size="sm" variant={mode === 'all' ? 'default' : 'outline'} onClick={() => { setMode('all'); setExpandedMatches(new Set()); setExpandedCats(new Set()); }} className={`h-7 text-xs px-3 ${mode === 'all' ? 'bg-[#C4A052] hover:bg-[#D4B86A] text-black' : 'text-[#C4A052] border-[#C4A052]/30'}`} data-testid="mode-all-btn">TOUT</Button>
          </div>
          <span className="text-sm text-zinc-400">
            <strong className={mode === 'need' ? 'text-red-400' : mode === 'stock' ? 'text-emerald-400' : (totalCount > 0 ? 'text-emerald-400' : totalCount < 0 ? 'text-red-400' : 'text-zinc-400')}>{mode === 'all' && totalCount > 0 ? '+' : ''}{totalCount}</strong> {mode === 'need' ? `billet${totalCount > 1 ? 's' : ''} NEED` : mode === 'stock' ? `billet${totalCount > 1 ? 's' : ''} en STOCK` : 'DELTA NET (Stock − Vente)'} · <strong className="text-white">{tree.length}</strong> match{tree.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={expandAll} className="h-7 text-xs px-2 border-zinc-600 text-zinc-300" data-testid="expand-all-btn">
            <ChevronsDown className="h-3.5 w-3.5 mr-1" /> Tout déplier
          </Button>
          <Button size="sm" variant="outline" onClick={collapseAll} className="h-7 text-xs px-2 border-zinc-600 text-zinc-300" data-testid="collapse-all-btn">
            <ChevronsUp className="h-3.5 w-3.5 mr-1" /> Tout replier
          </Button>
          <Button size="sm" variant="outline" onClick={() => doCopy('all', buildAllText())} className="h-7 text-xs px-2 border-zinc-600 text-zinc-300" data-testid="copy-all-btn">
            {copiedKey === 'all' ? <Check className="h-3.5 w-3.5 mr-1 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 mr-1" />} Copier tout
          </Button>
          <Button size="sm" onClick={exportCSV} className="h-7 text-xs px-2 bg-[#C4A052] hover:bg-[#D4B86A] text-black" data-testid="export-detail-csv">
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-700 rounded-lg overflow-hidden bg-black">
        {/* Header */}
        <div className={`grid ${mode === 'need' ? 'grid-cols-[28%_16%_7%_16%_10%]' : mode === 'stock' ? 'grid-cols-[24%_11%_9%_9%_9%_22%]' : 'grid-cols-[24%_8%_12%_7%_9%_9%_9%_10%]'} bg-zinc-700 text-xs font-bold text-zinc-300 sticky top-0 z-10`}>
          <div className="px-3 py-2">MATCH / CATÉGORIE</div>
          {mode === 'need' ? (
            <>
              <div className="px-3 py-2 text-center">REF DE VENTE</div>
              <div className="px-3 py-2 text-center">QTY</div>
              <div className="px-3 py-2 text-center">CATÉGORIE</div>
              <div className="px-3 py-2 text-right">PRIX</div>
            </>
          ) : mode === 'stock' ? (
            <>
              <div className="px-3 py-2 text-center">SOURCE</div>
              <div className="px-3 py-2 text-center">BLOCK</div>
              <div className="px-3 py-2 text-center">RANG</div>
              <div className="px-3 py-2 text-center">PLACE</div>
              <div className="px-3 py-2 text-center">EMAIL COMPTE</div>
            </>
          ) : (
            <>
              <div className="px-3 py-2 text-center">TYPE</div>
              <div className="px-3 py-2 text-center">REF VENTE</div>
              <div className="px-3 py-2 text-center">QTY</div>
              <div className="px-3 py-2 text-center">BLOCK</div>
              <div className="px-3 py-2 text-center">RANG</div>
              <div className="px-3 py-2 text-center">PLACE</div>
              <div className="px-3 py-2 text-right">PRIX</div>
            </>
          )}
        </div>

        <div className="max-h-[65vh] overflow-auto">
          {tree.map((match) => {
            const matchKey = `m${match.matchNum}`;
            const isMatchOpen = expandedMatches.has(matchKey);

            return (
              <div key={matchKey}>
                {/* Match row */}
                <div
                  className="grid grid-cols-[1fr_80px_80px_40px] bg-zinc-700 hover:bg-zinc-600 cursor-pointer border-t border-zinc-500 select-none"
                  onClick={() => toggleMatch(matchKey)}
                  data-testid={`detail-match-${match.matchNum}`}
                >
                  <div className="px-3 py-2 flex items-center gap-2">
                    {isMatchOpen ? <ChevronDown className="h-4 w-4 text-zinc-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-400 flex-shrink-0" />}
                    {(() => {
                      const tFR = getMatchTimeFR(match.matchNum);
                      const ev = resolveEventByApi(match.evenement, match.dateEvenement, { lieu: match.lieu, ville: match.ville, matchStartUTC: tFR?.startUTC || null });
                      return (<>
                        <span className="font-bold text-white text-sm">{ev.text}</span>
                        {ev.doute && (
                          <span
                            className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40"
                            title={ev.reason}
                            data-testid={`detail-doute-${match.matchNum}`}
                          >⚠ DOUTE</span>
                        )}
                      </>);
                    })()}
                    <span className={`text-xs font-bold ml-2 ${mode === 'stock' ? 'text-emerald-400' : mode === 'all' ? (match.matchTotalQty > 0 ? 'text-emerald-400' : match.matchTotalQty < 0 ? 'text-red-400' : 'text-zinc-400') : 'text-red-400'}`}>({match.matchTotalQty > 0 && mode === 'all' ? '+' : ''}{match.matchTotalQty})</span>
                  </div>
                  <div className="px-1 py-2 text-right">
                    <button className="text-zinc-500 hover:text-white" onClick={(e) => { e.stopPropagation(); doCopy(matchKey, buildMatchText(match)); }}>
                      {copiedKey === matchKey ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Categories */}
                {isMatchOpen && match.categories.map((cat) => {
                  const catKey = `${matchKey}_${cat.name}`;
                  const isCatOpen = expandedCats.has(catKey);

                  return (
                    <div key={catKey}>
                      {/* Category row */}
                      <div
                        className="grid grid-cols-[1fr_80px_80px_40px] bg-[#C4A052]/25 hover:bg-[#C4A052]/35 cursor-pointer border-t border-[#C4A052]/30 select-none"
                        onClick={() => toggleCat(catKey)}
                        data-testid={`detail-cat-${match.matchNum}-${cat.name}`}
                      >
                        <div className="px-3 py-1.5 pl-8 flex items-center gap-2">
                          {isCatOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#C4A052] flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-[#C4A052] flex-shrink-0" />}
                          <span className="font-bold text-[#C4A052] text-sm">{cat.name}</span>
                          <span className={`text-xs font-bold ml-1 ${mode === 'stock' ? 'text-emerald-400' : mode === 'all' ? (cat.totalQty > 0 ? 'text-emerald-400' : cat.totalQty < 0 ? 'text-red-400' : 'text-zinc-400') : 'text-red-400'}`}>{cat.totalQty > 0 && mode === 'all' ? '+' : ''}{cat.totalQty}</span>
                        </div>
                        <div className="px-1 py-1.5 text-right">
                          <button className="text-zinc-500 hover:text-[#C4A052]" onClick={(e) => { e.stopPropagation(); doCopy(catKey, buildCatText(match, cat)); }}>
                            {copiedKey === catKey ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          </button>
                        </div>
                      </div>

                      {/* ticket detail rows */}
                      {isCatOpen && cat.tickets.map((t, ti) => (
                        <div
                          key={t.id || ti}
                          className={`grid ${mode === 'need' ? 'grid-cols-[28%_16%_7%_16%_10%]' : mode === 'stock' ? 'grid-cols-[24%_11%_9%_9%_9%_22%]' : 'grid-cols-[24%_8%_12%_7%_9%_9%_9%_10%]'} ${mode === 'all' ? (t.type === 'VENTE' ? 'bg-red-950/60 hover:bg-red-900/60' : 'bg-emerald-950/60 hover:bg-emerald-900/60') : 'bg-zinc-800 hover:bg-zinc-750'} border-t border-zinc-700 text-sm`}
                        >
                          <div className="px-3 py-1.5 pl-14" />
                          {mode === 'need' ? (
                            <>
                              <div className="px-3 py-1.5 text-center font-mono text-white font-bold">{t.ref_vente || '-'}</div>
                              <div className="px-3 py-1.5 text-center text-white">{t.qty || 1}</div>
                              <div className="px-3 py-1.5 text-center text-zinc-400">{t.categorie || '-'}</div>
                              <div className="px-3 py-1.5 text-right text-[#C4A052] font-medium">{t.prix ? `${t.prix}` : '-'}</div>
                            </>
                          ) : mode === 'stock' ? (
                            <>
                              <div className="px-3 py-1.5 text-center text-zinc-300">{t.source || '-'}</div>
                              <div className="px-3 py-1.5 text-center text-[#C4A052] font-bold">{t.block || ''}</div>
                              <div className="px-3 py-1.5 text-center text-[#C4A052] font-bold">{t.rang || ''}</div>
                              <div className="px-3 py-1.5 text-center text-[#C4A052] font-bold">{t.place || ''}</div>
                              <div className="px-3 py-1.5 text-center text-zinc-400 truncate">{t.email_compte || '-'}</div>
                            </>
                          ) : (
                            <>
                              <div className="px-3 py-1.5 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.type === 'VENTE' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'}`}>{t.type}</span>
                              </div>
                              <div className="px-3 py-1.5 text-center font-mono text-white">{t.ref_vente || '-'}</div>
                              <div className="px-3 py-1.5 text-center text-white">{t.qty || 1}</div>
                              <div className="px-3 py-1.5 text-center text-[#C4A052] font-bold">{t.block || ''}</div>
                              <div className="px-3 py-1.5 text-center text-[#C4A052] font-bold">{t.rang || ''}</div>
                              <div className="px-3 py-1.5 text-center text-[#C4A052] font-bold">{t.place || ''}</div>
                              <div className="px-3 py-1.5 text-right text-[#C4A052]">{t.prix ? `${t.prix}` : '-'}</div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DeltaDetailView;
